<template>
  <v-container id="resource-form" class="position-relative">
    <app-city-header />

    <v-overlay :value="actionLoading" absolute>
      <v-progress-circular indeterminate color="primary" size="64" />
    </v-overlay>

    <v-form ref="formRef">
      <v-row class="mt-4">
        <v-col cols="12" md="12">
          <v-select
            v-model="selectedCriterion"
            :items="criteria"
            item-text="displayLabel"
            item-value="value"
            item-disabled="disabled"
            label="Critério"
            :disabled="isEdit"
            :rules="[rules.required]"
          >
            <template v-slot:item="{ item }">
              <span>{{ item.displayLabel }}</span>
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="item && index === 0">{{ item.displayLabel }}</span>
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="form.justificativa"
            label="Justificativa detalhada"
            auto-grow
            counter="10000"
            :rules="[rules.required, rules.maxJustificativa]"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <h3 class="subtitle-1 font-weight-medium">Anexos enviados</h3>
          <div v-if="existingAttachments.length">
            <v-chip
              v-for="anexo in existingAttachments"
              :key="anexo.id"
              class="ma-1"
              close
              outlined
              @click:close="removeExistingAttachment(anexo)"
            >
              {{ anexo.nome }}
            </v-chip>
          </div>
          <div v-else class="grey--text">Nenhum anexo cadastrado.</div>
        </v-col>
        <v-col cols="12" md="6">
          <v-file-input
            v-model="form.arquivos"
            label="Adicionar anexos"
            multiple
            show-size
            truncate-length="25"
            :rules="[rules.fileSize]"
          />
        </v-col>
      </v-row>

      <v-row class="mt-6">
        <v-col cols="12" class="text-right">
          <v-btn text color="secondary" class="mr-2" @click="voltar">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            class="mr-2"
            :loading="saving"
            @click="salvar(false)"
            :disabled="!isEditable || actionLoading"
          >
            Salvar rascunho
          </v-btn>
          <v-btn
            color="success"
            :loading="sending"
            @click="salvar(true)"
            :disabled="!isEditable || actionLoading"
          >
            Enviar para análise
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { AppCityHeader } from "@/modules/city";
import { resourceAPI } from "../API/ResourceAPI";
import { buildCriterionDisplayLabel } from "@/helpers/criteria";
import { findStatusMeta } from "../constants";

export default {
  name: "ResourceForm",
  components: { AppCityHeader },
  data() {
    return {
      loading: false,
      saving: false,
      sending: false,
      criteria: [],
      selectedCriterion: null,
      form: {
        seloRecursoId: null,
        municipioId: this.$route.params.municipioId,
        perguntaId: null,
        perguntaMunicipioId: null,
        ehPerguntaClienteOculto: false,
        notaOriginal: "",
        notaPleiteada: "",
        justificativa: "",
        arquivos: [],
      },
      existingAttachments: [],
      attachmentsToRemove: [],
      currentStatus: "RASCUNHO",
      config: JSON.parse(localStorage.getItem("config")) || {},
      groupsConfig: JSON.parse(localStorage.getItem("groupsConfig")) || {},
      actionLoading: false,
      rules: {
        required: (value) => !!value || "Campo obrigatório",
        maxJustificativa: (value) =>
          !value || value.length <= 10000 || "Máximo de 10000 caracteres",
        fileSize: () => true,
      },
    };
  },
  computed: {
    ...mapGetters(["isFullAdmin", "cityId"]),
    routeRecursoId() {
      const rawId = this.$route.params.recursoId;
      if (!rawId) {
        return null;
      }
      const parsedId = Number(rawId);
      return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
    },
    isEdit() {
      return this.routeRecursoId !== null;
    },
    municipioId() {
      return this.$route.params.municipioId || this.cityId;
    },
    canCreate() {
      if (this.isFullAdmin) {
        return true;
      }

      const podeAcessarRecursos =
        (this.config.recursos ?? false) &&
        (this.groupsConfig.recursos ?? false);

      const podeCriarRecursos =
        (this.config.criarRecursos ?? false) &&
        (this.groupsConfig.criarRecursos ?? false);

      return podeAcessarRecursos && podeCriarRecursos;
    },
    canManage() {
      return (
        this.isFullAdmin ||
        ((this.config.recursos ?? false) && (this.groupsConfig.recursos ?? false))
      );
    },
    isEditable() {
      if (!this.canManage) {
        return false;
      }

      if (!this.isEdit && !this.canCreate) {
        return false;
      }
      return ["RASCUNHO", "COMPLEMENTAR"].includes(this.currentStatus);
    },
  },
  created() {
    if (!this.isEdit && !this.canCreate) {
      this.notifyError("Você não tem permissão para criar novos recursos.");
      this.$router.replace(`/cidade/${this.municipioId}/recursos`);
      return;
    }

    this.carregarCriterios();
    if (this.isEdit) {
      this.carregarRecurso();
    }
  },
  watch: {
    selectedCriterion(newValue) {
      if (!newValue) {
        this.form.perguntaId = null;
        this.form.perguntaMunicipioId = null;
        this.form.ehPerguntaClienteOculto = false;
        this.form.notaOriginal = "";
        this.form.notaPleiteada = "";
        return;
      }

      const criterio = this.criteria.find(
        (item) => item.value === newValue
      );
      if (criterio) {
        const perguntaId = this.normalizeIntegerId(criterio.perguntaId);
        this.form.perguntaId = perguntaId ?? criterio.perguntaId ?? null;
        this.form.perguntaMunicipioId =
          this.normalizeIntegerId(criterio.perguntaMunicipioId) ??
          criterio.perguntaMunicipioId ??
          null;
        const ehClienteOculto = this.normalizeClienteOcultoFlag(
          criterio.ehPerguntaClienteOculto
        );
        this.form.ehPerguntaClienteOculto = ehClienteOculto;
        const nota = Number(criterio.notaOriginal ?? 0);
        this.form.notaOriginal = Number.isFinite(nota)
          ? nota.toFixed(2)
          : "0.00";
        this.form.notaPleiteada = this.form.notaOriginal;
      }
    },
  },
  methods: {
    buildCriterionValue(perguntaId, ehPerguntaClienteOculto, perguntaMunicipioId) {
      const normalizedPerguntaId = this.normalizeIntegerId(perguntaId);
      const normalizedPerguntaMunicipioId = this.normalizeIntegerId(
        perguntaMunicipioId
      );
      const id = normalizedPerguntaId ?? perguntaId ?? "";
      const municipioId =
        normalizedPerguntaMunicipioId ??
        normalizedPerguntaId ??
        perguntaMunicipioId ??
        perguntaId ??
        "";
      const prefix = ehPerguntaClienteOculto ? "clienteoculto" : "pergunta";
      return `${prefix}-${municipioId}-${id}`;
    },
    normalizeClienteOcultoFlag(value) {
      if (value === true || value === 1) {
        return true;
      }

      if (value === false || value === 0) {
        return false;
      }

      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "sim", "yes"].includes(normalized)) {
          return true;
        }
        if (["0", "false", "nao", "não", "no"].includes(normalized)) {
          return false;
        }
      }

      return Boolean(value);
    },
    syncSelectedCriterion() {
      if (!this.form.perguntaId || !this.criteria.length) {
        return;
      }

      const formPerguntaId = this.normalizeIntegerId(this.form.perguntaId);
      const ehClienteOculto = this.normalizeClienteOcultoFlag(
        this.form.ehPerguntaClienteOculto
      );

      const criterio = this.criteria.find((item) => {
        const itemPerguntaId = this.normalizeIntegerId(item.perguntaId);
        const itemEhClienteOculto = this.normalizeClienteOcultoFlag(
          item.ehPerguntaClienteOculto
        );
        return (
          (itemPerguntaId ?? item.perguntaId) ===
            (formPerguntaId ?? this.form.perguntaId) &&
          itemEhClienteOculto === ehClienteOculto
        );
      });

      if (criterio) {
        this.form.perguntaMunicipioId = criterio.perguntaMunicipioId ?? null;
        this.selectedCriterion = criterio.value;
        this.form.ehPerguntaClienteOculto = ehClienteOculto;
      }
    },
    notifySuccess(message) {
      if (this.$toast?.success) {
        this.$toast.success(message);
      } else if (this.$notification?.success) {
        this.$notification.success(message);
      } else {
        console.info(message);
      }
    },
    notifyError(message) {
      if (this.$toast?.error) {
        this.$toast.error(message);
      } else if (this.$notification?.error) {
        this.$notification.error(message);
      } else {
        console.error(message);
      }
    },
    async carregarCriterios() {
      try {
        const { data } = await resourceAPI.criteria(this.municipioId);
        const lista = Array.isArray(data) ? data : [];
        this.criteria = lista.map((item) => {
          const perguntaIdRaw = item.PerguntaId ?? item.perguntaId ?? null;
          const perguntaMunicipioIdRaw =
            item.PerguntaMunicipioId ?? item.perguntaMunicipioId ?? null;
          const perguntaId = this.normalizeIntegerId(perguntaIdRaw);
          const perguntaMunicipioId = this.normalizeIntegerId(
            perguntaMunicipioIdRaw
          );
          const texto = item.PerguntaTitulo ?? item.perguntaTitulo;
          const grupoNome = item.GrupoNome ?? item.grupoNome;
          const rawLabel = grupoNome ? `${grupoNome} - ${texto}` : texto;
          const displayLabel = buildCriterionDisplayLabel(rawLabel);
          const notaOriginal = Number(
            item.NotaOriginal ?? item.notaOriginal ?? 0
          ).toFixed(2);
          const bloqueado =
            item.PossuiRecursoEmAberto ?? item.possuiRecursoEmAberto;
          const ehPerguntaClienteOculto = this.normalizeClienteOcultoFlag(
            item.EhPerguntaClienteOculto ?? item.ehPerguntaClienteOculto ?? false
          );
          const value = this.buildCriterionValue(
            perguntaId ?? perguntaIdRaw,
            ehPerguntaClienteOculto,
            perguntaMunicipioId ?? perguntaMunicipioIdRaw
          );
          return {
            value,
            perguntaId: perguntaId ?? perguntaIdRaw ?? null,
            perguntaMunicipioId:
              perguntaMunicipioId ?? perguntaMunicipioIdRaw ?? null,
            label: rawLabel,
            displayLabel,
            grupoNome,
            notaOriginal,
            disabled: !!bloqueado,
            ehPerguntaClienteOculto,
          };
        });

        this.syncSelectedCriterion();
      } catch (error) {
        console.error("Erro ao carregar critérios", error);
        this.notifyError("Não foi possível carregar os critérios elegíveis");
      }
    },
    async carregarRecurso() {
      this.loading = true;
      try {
        const recursoId = this.routeRecursoId;
        if (!recursoId) {
          this.notifyError("Recurso inválido");
          this.$router.replace(`/cidade/${this.municipioId}/recursos`);
          return;
        }

        const { data } = await resourceAPI.get(recursoId);
        const cabecalho = data.Cabecalho || data.cabecalho;
        if (!cabecalho) {
          throw new Error("Dados do recurso inválidos");
        }

        const statusMeta = findStatusMeta(cabecalho.Status || cabecalho.status);
        this.currentStatus = statusMeta.value;

        if (!this.isEditable) {
          this.$router.replace(
            `/cidade/${this.municipioId}/recursos/${cabecalho.SeloRecursoId}`
          );
          return;
        }

        this.form.seloRecursoId = cabecalho.SeloRecursoId;
        const municipioId =
          this.normalizeIntegerId(
            cabecalho.MunicipioId ?? cabecalho.municipioId ?? null
          ) ?? (cabecalho.MunicipioId ?? cabecalho.municipioId ?? null);
        this.form.municipioId = municipioId;
        const perguntaId = this.normalizeIntegerId(
          cabecalho.PerguntaId ?? cabecalho.perguntaId ?? null
        );
        this.form.perguntaId = perguntaId ?? cabecalho.PerguntaId ?? cabecalho.perguntaId;
        this.form.perguntaMunicipioId = null;
        const ehClienteOculto = this.normalizeClienteOcultoFlag(
          cabecalho.EhPerguntaClienteOculto ??
            cabecalho.ehPerguntaClienteOculto ??
            false
        );
        this.form.ehPerguntaClienteOculto = ehClienteOculto;
        this.syncSelectedCriterion();
        this.form.notaOriginal = Number(
          cabecalho.NotaOriginal ?? cabecalho.notaOriginal ?? 0
        ).toFixed(2);
        const notaPleiteada = Number(
          cabecalho.NotaPleiteada ?? cabecalho.notaPleiteada ?? this.form.notaOriginal
        );
        this.form.notaPleiteada = Number.isFinite(notaPleiteada)
          ? notaPleiteada.toFixed(2)
          : this.form.notaOriginal;
        this.form.justificativa = data.Justificativa || data.justificativa || "";

        const anexos = data.Anexos || data.anexos || [];
        this.existingAttachments = anexos.map((item) => ({
          id: item.SeloRecursoAnexoId ?? item.seloRecursoAnexoId,
          nome: item.Nome ?? item.nome,
        }));
      } catch (error) {
        console.error("Erro ao carregar recurso", error);
        this.notifyError("Não foi possível carregar o recurso");
        this.$router.replace(`/cidade/${this.municipioId}/recursos`);
      } finally {
        this.loading = false;
      }
    },
    removeExistingAttachment(anexo) {
      this.attachmentsToRemove.push(anexo.id);
      this.existingAttachments = this.existingAttachments.filter(
        (item) => item.id !== anexo.id
      );
    },
    async salvar(sendAfterSave) {
      if (!this.isEditable) {
        this.notifyError("Este recurso não pode mais ser editado");
        return;
      }

      if (!this.$refs.formRef.validate()) {
        return;
      }

      if (sendAfterSave && !this.validarEnvio()) {
        return;
      }

      if (this.actionLoading) {
        return;
      }

      this.saving = !sendAfterSave;
      this.sending = sendAfterSave;

      this.actionLoading = true;

      try {
        const recursoIdFromState =
          this.form.seloRecursoId !== null && this.form.seloRecursoId !== undefined
            ? Number(this.form.seloRecursoId)
            : null;
        const recursoId =
          recursoIdFromState && !Number.isNaN(recursoIdFromState)
            ? recursoIdFromState
            : this.routeRecursoId;

        if (this.isEdit && (!recursoId || Number.isNaN(recursoId))) {
          this.notifyError("Recurso inválido");
          return;
        }

        const notaPleiteadaValor =
          this.form.notaPleiteada !== ""
            ? this.form.notaPleiteada
            : this.form.notaOriginal || 0;

        const perguntaId = this.normalizeIntegerId(this.form.perguntaId);
        if (!perguntaId) {
          this.notifyError("Selecione um critério válido antes de salvar");
          return;
        }

        const municipioId =
          this.normalizeIntegerId(this.form.municipioId) ??
          Number(this.form.municipioId);

        const payload = {
          SeloRecursoId: recursoId || 0,
          MunicipioId: municipioId || 0,
          PerguntaId: perguntaId,
          EhPerguntaClienteOculto: this.normalizeClienteOcultoFlag(
            this.form.ehPerguntaClienteOculto
          ),
          NotaOriginal: this.normalizeDecimalForApi(this.form.notaOriginal),
          NotaPleiteada: this.normalizeDecimalForApi(notaPleiteadaValor),
          Justificativa: this.form.justificativa,
          AnexosRemovidos: this.attachmentsToRemove.join(","),
          arquivos: this.form.arquivos || [],
        };

        let response;
        let finalId = recursoId || 0;

        if (this.isEdit) {
          response = await resourceAPI.update(finalId, payload);
        } else {
          response = await resourceAPI.create(payload);
          finalId = Number(
            response?.data?.SeloRecursoId ?? response?.data?.seloRecursoId ?? 0
          );
          this.form.seloRecursoId = finalId || null;
        }

        if (sendAfterSave) {
          if (!finalId) {
            this.notifyError("Não foi possível identificar o recurso para envio");
            return;
          }

          await resourceAPI.send(finalId, {});
        }

        this.form.seloRecursoId = finalId;

        this.notifySuccess(
          sendAfterSave
            ? "Recurso enviado para análise com sucesso"
            : "Recurso salvo com sucesso"
        );

        this.$router.push(
          `/cidade/${this.municipioId}/recursos/${this.form.seloRecursoId}`
        );
      } catch (error) {
        console.error("Erro ao salvar recurso", error);
        this.notifyError("Não foi possível salvar o recurso");
      } finally {
        this.saving = false;
        this.sending = false;
        this.actionLoading = false;
      }
    },
    validarEnvio() {
      if (!this.form.justificativa) {
        this.notifyError("Informe a justificativa antes de enviar");
        return false;
      }

      return true;
    },
    normalizeIntegerId(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      if (typeof value === "number" && Number.isInteger(value) && value > 0) {
        return value;
      }

      const numeric = Number(value);
      if (Number.isInteger(numeric) && numeric > 0) {
        return numeric;
      }

      const parsed = Number.parseInt(value, 10);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    },
    normalizeDecimalForApi(value) {
      if (value === null || value === undefined || value === "") {
        return "0";
      }

      const numericValue = Number(
        typeof value === "string" ? value.replace(",", ".") : value
      );

      if (!Number.isFinite(numericValue)) {
        return "0";
      }

      return numericValue.toFixed(2).replace(".", ",");
    },
    voltar() {
      this.$router.push(`/cidade/${this.municipioId}/recursos`);
    },
  },
};
</script>

<style scoped>
#resource-form {
  min-height: 80vh;
}

.position-relative {
  position: relative;
}
</style>

